import { PlannerEcsSystem } from '@inter-ikea-storageone/range';
import { ComponentIds, HierarchyUtils, TransformUtils } from '@inter-ikea-core/core';
import { BestaFrameAssemblySystem } from './BestaFrameAssemblySystem';
import { BestaFrameSetLegsOnCreatedSystem } from './BestaFrameSetLegsOnCreatedSystem';
import { FrameSelectedLegComponent } from '../components/FrameSelectedLegComponent';
import { BestaLegsComponent } from '../components/BestaLegsComponent';
import { BestaFrameLegsCalculatorSystem } from './BestaFrameLegsCalculatorSystem';
/**
 * A system of systems, this system will create other systems that it needs to solve the feature
 * BESTÅ legs
 */
export class BestaFrameLegsFeatureSystem extends PlannerEcsSystem {
    get selectedLeg() {
        const selectedLeg = this.coreApi.entities
            ?.getByComponent(BestaLegsComponent.id)[0]
            ?.getComponent(BestaLegsComponent.id);
        if (selectedLeg) {
            return selectedLeg.legProductRef;
        }
        return this.coreApi.products.getDefaultLeg(); // This can happen if e.g. the UI samples this property when there is no room
    }
    set selectedLeg(legProductRef) {
        this.performUpdateToNewLegAction(legProductRef, this.useSupportLeg, false);
    }
    get useSupportLeg() {
        const useSupportLeg = this.coreApi.entities
            ?.getByComponent(FrameSelectedLegComponent.id)[0]
            ?.getComponent(FrameSelectedLegComponent.id)?.useSupportLeg;
        return useSupportLeg ? useSupportLeg : false;
    }
    set useSupportLeg(v) {
        this.performUpdateToNewLegAction(this.selectedLeg, v, false);
    }
    constructor(coreApi) {
        super(coreApi, 'BestaFrameLegsFeatureSystem', ComponentIds.IN_SCENE_COMPONENT_ID);
        this.core = coreApi.core;
        this.coreApi = coreApi;
        this.entityManager = this.core.services.entityManager;
        const defaultSelectedLeg = new FrameSelectedLegComponent(this.coreApi.products.getDefaultLeg(), false);
        this.setLegsOnCreatedFramesSystem = new BestaFrameSetLegsOnCreatedSystem(this.coreApi, defaultSelectedLeg);
        this.frameLegsCalculatorSystem = new BestaFrameLegsCalculatorSystem(this.coreApi, ComponentIds.FRAME_COMPONENT_ID);
        this.frameAssemblySystem = new BestaFrameAssemblySystem(this.coreApi);
        coreApi.registerSystem(this.setLegsOnCreatedFramesSystem);
        coreApi.registerSystem(this.frameLegsCalculatorSystem);
        coreApi.registerSystem(this.frameAssemblySystem);
        class LegComponentSerializer {
            constructor() {
                this.thisClassIsGuaranteedToNotModifyOrQueryCoreState = true;
            }
            canHandle(serializationKey) {
                return serializationKey === 'FrameSelectedLegComponent';
            }
            deserialize(serializationKey, json) {
                const data = JSON.parse(json.toString());
                switch (serializationKey) {
                    case 'FrameSelectedLegComponent':
                        return new FrameSelectedLegComponent(data.legProductRef, data.useSupportLeg || false);
                }
                return null;
            }
        }
        const componentDeserializerRegistry = this.core.services.componentDeserializerRegistry;
        componentDeserializerRegistry.register(new LegComponentSerializer());
    }
    register() { }
    enableFeature() {
        this.enable();
        this.setLegsOnCreatedFramesSystem.enable();
        this.frameLegsCalculatorSystem.enable();
        this.frameAssemblySystem.enable();
        // Just to make sure the room has a fresh component for which legs we should use, and any systems depending on it reset their state
        const scene = this.getScene();
        const selectedLeg = scene?.getComponent(FrameSelectedLegComponent.id) ||
            new FrameSelectedLegComponent(this.coreApi.products.getDefaultLeg(), false);
        this.entityManager.removeComponent(scene, FrameSelectedLegComponent.id);
        this.entityManager.addComponent(scene, selectedLeg);
    }
    disableFeature() {
        this.disable();
        this.setLegsOnCreatedFramesSystem.disable();
        this.frameLegsCalculatorSystem.disable();
        this.frameAssemblySystem.disable();
    }
    reset() {
        const scene = this.getScene();
        if (!scene)
            return;
        this.entityManager.removeComponent(scene, FrameSelectedLegComponent.id);
        this.entityManager.addComponent(scene, new FrameSelectedLegComponent(this.coreApi.products.getDefaultLeg(), false));
    }
    onAdd(entity) {
        if (entity.hasComponent(ComponentIds.SCENE_COMPONENT_ID) && this.isEnabled) {
            this.onAfterLoad();
        }
    }
    onUpdate() { }
    onRemove() { }
    onBeforeLoad() {
        this.disableFeature();
    }
    onAfterLoad() {
        // Get the leg data
        const scene = this.getScene();
        if (!scene)
            return;
        let selectedLeg = scene?.getComponent(FrameSelectedLegComponent.id);
        // If we are loading an old vpc code we need to remove the legs components from the frame so we get entities
        // with the correct components added after the systems have been initialized.
        // If we kept these components we ended up getting some weird bugs (VPC codes created prior to feb/mars 2021)
        // New vpc codes created after this date should work without this part
        if (!selectedLeg) {
            selectedLeg = new FrameSelectedLegComponent(this.coreApi.products.getDefaultLeg(), true);
        }
        // Force trigger a new height calculation
        this.entityManager.removeComponent(scene, FrameSelectedLegComponent.id);
        this.entityManager.addComponent(scene, selectedLeg);
        const allFramesInScene = this.coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID, 'besta');
        this.frameLegsCalculatorSystem.onAfterLoad(selectedLeg, allFramesInScene);
        this.enableFeature();
    }
    getScene() {
        return this.coreApi.entities.getScene();
    }
    setSelectedLeg(productRef, useSupportLeg) {
        return this.performUpdateToNewLegAction(productRef, useSupportLeg, false);
    }
    changeSupportLeg(productRef, useSupportLeg, onlyUpdateSelectedFrame) {
        this.performUpdateToNewLegAction(productRef, useSupportLeg, onlyUpdateSelectedFrame);
    }
    /**
     * Set new leg for all frames and create one history item
     * @param leg The productRef for the new leg
     */
    performUpdateToNewLegAction(productRef, useSupportLeg, onlyUpdateSelectedFrame) {
        return this.coreApi.history.historyAction(() => {
            this.entityManager.addComponent(this.getScene(), new FrameSelectedLegComponent(productRef, useSupportLeg));
            if (onlyUpdateSelectedFrame) {
                this.updateLegsOnSelectedFrame(productRef, useSupportLeg);
                return undefined;
            }
            else {
                return this.updateLegsOnExistingFrames(productRef, useSupportLeg);
            }
        });
    }
    updateLegsOnSelectedFrame(productRef, useSupportLeg) {
        const selected = this.coreApi.entities.findSelectedEntity();
        if (!selected)
            return;
        const frame = selected.hasComponent(ComponentIds.FRAME_COMPONENT_ID)
            ? selected
            : HierarchyUtils.findInUpwardsHierarchy(selected, (entity) => entity.hasComponent(BestaLegsComponent.id));
        if (frame) {
            const oldComponent = frame.getComponent(BestaLegsComponent.id);
            if (!oldComponent) {
                throw this.coreApi.reportErrorToSentry(new Error(`Frame ${frame} lost its BestaLegsComponent which is an error`), { entities: { frame }, productRef, useSupportLeg });
            }
            const newComponent = oldComponent.withLegProductRef(productRef, useSupportLeg);
            this.core.services.entityManager.addComponent(frame, newComponent);
        }
    }
    updateLegsOnExistingFrames(productRef, useSupportLeg) {
        const frames = this.coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID, 'besta');
        const floorstandingFrames = frames.filter(frame => this.coreApi.entities.isEntityPlacedOnFloor(frame));
        const legs = this.coreApi.entities.getByComponent(ComponentIds.LEG_COMPONENT_ID, 'besta');
        this.coreApi.entities.deleteMultiple(legs); // Delete all legs while we resnap so merged legs doesn't lead to collision
        this.disableFeature();
        const resnapResults = [];
        floorstandingFrames.forEach((frameEntity) => {
            const placementResult = this.resnapFrame(frameEntity);
            resnapResults.push(placementResult);
        });
        this.enableFeature();
        frames.forEach((frameEntity) => {
            const oldComponent = frameEntity.getComponent(BestaLegsComponent.id);
            if (!oldComponent) {
                throw this.coreApi.reportErrorToSentry(new Error(`Frame ${frameEntity} lost its BestaLegsComponent which is an error`), {
                    productRef,
                    useSupportLeg,
                    entities: {
                        frameEntity,
                        frames,
                        legs
                    }
                });
            }
            const newComponent = oldComponent.withLegProductRef(productRef, useSupportLeg);
            frameEntity.removeComponent(oldComponent.typeId);
            frameEntity.addComponent(newComponent);
        });
        if (resnapResults.some(result => result?.valid === false)) {
            // If not possible to resnap to new position (e.g. colliding with roof or hanging frames) we undo leg change
            this.coreApi.history.undo();
            this.coreApi.history.discardLatestSnapshot();
        }
        return resnapResults;
    }
    resnapFrame(frame) {
        const position = TransformUtils.worldPosition(frame);
        return this.coreApi.placementService.snapToPosition(frame, position, true);
    }
}
//# sourceMappingURL=BestaFrameLegsFeatureSystem.js.map